<template>
  <div class="uploader-container">
    <!-- Unclickable Button Drop Zone Container -->
    <div
      class="dropzone-button"
      :class="{ 'is-dragover': isDragOver, 'disabled': isDisabled }"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <span v-if="!file">📂 Drag .tar File Here</span>
      <span v-else class="file-name">📄 {{ file.name }} ({{ formatSize(file.size) }})</span>
    </div>

    <!-- Error Message Display -->
    <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>

    <!-- Upload Progress Tracking Area -->
    <div v-if="file" class="progress-container">
      <div class="progress-bar-wrapper">
        <div class="progress-bar" :style="{ width: progress + '%' }"></div>
      </div>
      <div class="progress-details">
        <span>{{ progress }}% Uploaded</span>
        <button
          v-if="!isUploading && progress === 100"
          @click="removeFile"
          class="clear-btn"
        >
          Remove
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DropZone',
  props: {
    // Parent passes the active file reference down
    file: {
      type: File,
      default: null
    },
    // Parent passes the reactive upload numerical progress down
    progress: {
      type: Number,
      default: 0
    },
    // Parent passes down the status state
    isUploading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDragOver: false,
      errorMessage: ''
    };
  },
  computed: {
    isDisabled() {
      return !!this.file || this.isUploading;
    }
  },
  methods: {
    onDragOver() {
      if (!this.isDisabled) {
        this.isDragOver = true;
      }
    },
    onDragLeave() {
      this.isDragOver = false;
    },
    onDrop(event) {
      this.isDragOver = false;
      if (this.isDisabled) return;

      const droppedFiles = event.dataTransfer.files;
      if (droppedFiles.length > 0) {
        this.processFile(droppedFiles[0]); // Force 1-file limit
      }
    },
    processFile(file) {
      this.errorMessage = '';

      if (!file.name.endsWith('.tar')) {
        this.errorMessage = 'Invalid file type. Only .tar archives are allowed.';
        return;
      }

      // Emit file payload to the parent component
      this.$emit('file-selected', file);
    },
    removeFile() {
      this.errorMessage = '';
      this.$emit('file-removed');
    },
    formatSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
.uploader-container {
  max-width: 400px;
  margin: 0 auto;
  margin-right: 8px;
  font-family: Arial, sans-serif;
}

.dropzone-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #3b82f6;
  color: white;
  padding: 7px 24px;
  font-weight: 500;
  border-radius: 6px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s ease;
  cursor: default;
  user-select: none;
}

.dropzone-button.is-dragover {
  background-color: #2563eb;
  outline: 2px dashed #1d4ed8;
  outline-offset: 4px;
}

.dropzone-button.disabled {
  background-color: #94a3b8;
  color: #f1f5f9;
}

.error-text {
  color: #ef4444;
  font-size: 14px;
  margin-top: 8px;
  text-align: center;
}

.progress-container {
  margin-top: 20px;
}

.progress-bar-wrapper {
  background-color: #e2e8f0;
  border-radius: 9999px;
  height: 8px;
  overflow: hidden;
}

.progress-bar {
  background-color: #10b981;
  height: 100%;
  width: 0%;
  transition: width 0.2s ease-out;
}

.progress-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  font-size: 14px;
  color: #64748b;
}

.clear-btn {
  background-color: #ef4444;
  color: white;
  border: none;
  padding: 2px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.clear-btn:hover {
  background-color: #dc2626;
}
</style>
